/**
 * FormulaParser — the substitution chain, verbatim from the Phase-2
 * single-file port (pricing/price-calculation.ts), itself a verbatim port of
 * v1 price_calculation.js (REQ-FORM-2..5; research/04 §2.3–§2.5).
 *
 * Pass order is load-bearing: lowercase → math. → shopify_product_price →
 * known metafields → unmatched shopify_meta_* → collect .calc-prop →
 * longest-first sort → per-element substitution → lookup tables → return;
 * catch → globalHelper_ReportError(e) → implicit undefined.
 *
 * The duplicate `var allElements` line of v1 is compile-time-invisible and
 * not reproduced (REQ-FORM-2 note 6) — kept exactly as the Phase-2 port had
 * it (single occurrence would change nothing; the port carried both, so both
 * stay).
 */
import { appState } from "../state/app-state";
import { globalHelper_ReportError } from "../api/logs-error";
import { generateSafeRegex } from "./safe-regex";
import { resolveLookupTables } from "./lookup-tables";
import $ from "jquery";

export function FormulaParser(formula: any): any {
  try {
    formula = formula.toLowerCase();
    var mathRegexPattern = new RegExp("math.", "g");
    formula = !!formula
      ? formula.toString().replaceAll(mathRegexPattern, "Math.")
      : "";
    formula = !!formula
      ? formula
        .toString()
        .replaceAll("shopify_product_price", appState.cpc_product_default_price)
      : "";

    //apply meta fields
    appState.cpc_metaFields.forEach((item: any) => {
      formula = !!formula
        ? formula.toString().replaceAll("shopify_meta_" + item.key, item.value)
        : "";
    });

    //Apply to remaining meta fields
    var metaRegexPattern = new RegExp("shopify_meta_(.*?)([*/+-]|$)", "g");
    let m;
    do {
      m = metaRegexPattern.exec(formula);
      if (m) {
        if (m.length > 1) {
          var metaVariableName = m[1];
          formula = !!formula
            ? formula
              .toString()
              .replaceAll("shopify_meta_" + metaVariableName, 1)
            : "";
          console.log("meta_variable_name", metaVariableName);
        }
      }
    } while (m);

    //formula = formula.replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "")
    var allElements = $($(".calc-prop").get());
    var allElements = $($(".calc-prop").get());
    // jQuery.fn.sort is real but undocumented/untyped (Array.prototype.sort
    // borrowed onto the prototype) — the `any` keeps the exact v1 call.
    allElements = (allElements as any).sort((a: any, b: any) => {
      if ($(a).data("identifier") === undefined) return 1;
      else if ($(b).data("identifier") === undefined) return -1;
      else
        return $(a).data("identifier").length >
          $(b).data("identifier").length
          ? -1
          : 1;
    });

    var lookupElements: any = {};
    allElements.each((d: any, elem: any) => {
      let isHidden = $(elem).is(":hidden");
      let isCheckBox = $(elem).is(":checkbox");
      let isRadio = $(elem).is(":radio");
      var elementType = $(elem).prop("nodeName");
      let identifier = $(elem).data("identifier");
      let rangeEnabled = $(elem).data("rangeenabled");
      let lengthToggle = $(elem).data("lengthtoggle");
      let isLookupElement = $(elem).data("lookup") !== undefined;
      let IsLookupFormulaElement = isLookupElement
        ? $(elem).data("isformula") === true
        : false;
      // For formula-based lookup inputs, the .element wrapper carries the
      // hide-element CSS class (so getComputedStyle always returns display:none
      // on the input due to the !important rule). ProcessConditionalElements
      // toggles the wrapper's *inline* style.display via .show()/.hide().
      // Reading the inline style is the only reliable way to know whether this
      // lookup table is currently "active" for the selected option.
      let isLookupFormulaActive = false;
      if (IsLookupFormulaElement) {
        var $lookupWrapper = $(elem).closest(".element");
        isLookupFormulaActive = $lookupWrapper.length === 0 ||
          $lookupWrapper[0].style.display !== "none";
      }
      // console.log('isLookupElement', isLookupElement);
      if (identifier === undefined) return;
      if (identifier !== undefined) {
        var regexPattern = generateSafeRegex(identifier);

        if (isHidden === false || isLookupFormulaActive) {
          if (elementType == "SELECT") {
            let value = $(elem).children("option:selected").data("val");
            formula = formula.replace(regexPattern, value);
          } else if (elementType == "INPUT" && rangeEnabled === true) {
            let value: any = $(elem).val();
            value = parseFloat(value);

            var options = appState.cpc_data.Elements;
            let finalInputValue = value;
            options.forEach((element: any) => {
              if (element.Identifier === identifier) {
                var rangeOptions = element.options;
                let matchedRange = false;
                rangeOptions.forEach((element: any) => {
                  if (
                    value >= parseFloat(element.start) &&
                    value <= parseFloat(element.end)
                  ) {
                    finalInputValue = parseFloat(element.value);
                    matchedRange = true;
                  }
                });
                // Issue #81: a quantity outside every configured tier fell through
                // to the raw entered value, pricing off a number the merchant never
                // configured a tier for. Clamp to the nearest boundary tier instead —
                // but only among "real" tiers (end > start, the same rule the builder
                // itself uses to accept a saved range row, NumberInputEditor.vue's
                // isValueRangesValid). A zero-width row (start === end, most commonly
                // the builder's own untouched 0-0/value-0 placeholder left after
                // toggling "Value range" on) is excluded from the boundary search —
                // treating it as a real edge clamped every input to that placeholder's
                // value and froze the price (DIV-017 regression, DIV-021 revert,
                // Havehandel.dk). A config with no real tiers at all behaves exactly
                // like DIV-021: no clamp, raw value passes through.
                var realRangeOptions = rangeOptions.filter(
                  (o: any) => parseFloat(o.end) > parseFloat(o.start),
                );
                if (!matchedRange && realRangeOptions.length > 0 && !isNaN(value)) {
                  var lowestRange = realRangeOptions[0];
                  var highestRange = realRangeOptions[0];
                  realRangeOptions.forEach((rangeOption: any) => {
                    if (parseFloat(rangeOption.start) < parseFloat(lowestRange.start)) {
                      lowestRange = rangeOption;
                    }
                    if (parseFloat(rangeOption.end) > parseFloat(highestRange.end)) {
                      highestRange = rangeOption;
                    }
                  });
                  if (value < parseFloat(lowestRange.start)) {
                    finalInputValue = parseFloat(lowestRange.value);
                  } else if (value > parseFloat(highestRange.end)) {
                    finalInputValue = parseFloat(highestRange.value);
                  }
                }
              }
            });

            //Handle case for actual value
            let actualValueIdentifier = identifier + "[ActualValue]";
            var actualValueRegex = generateSafeRegex(actualValueIdentifier);
            formula = formula.replace(actualValueRegex, value);

            //Range handling
            finalInputValue = (parseFloat as any)(finalInputValue, 10);
            formula = formula.replace(regexPattern, finalInputValue);
          }
          if (elementType == "TEXTAREA") {
            let value: any = $(elem).val();
            let shouldCountWhiteSpace = lengthToggle;

            if (!shouldCountWhiteSpace) {
              // Remove white spaces from the value
              value = value.replace(/\s/g, "");
            }

            let length = value.length;

            let lengthIdentifier = identifier + "[length]";
            var lengthRegex = generateSafeRegex(lengthIdentifier);
            formula = formula.replace(lengthRegex, length);
          } else if (isCheckBox === true) {
            let isChecked = $(elem).is(":checked");
            let checkedValue = $(elem).data("checkedvalue");
            let uncheckedValue = $(elem).data("uncheckedvalue");
            var valueToBeUsed = isChecked ? checkedValue : uncheckedValue;
            //console.log('checkedValue', valueToBeUsed)
            formula = formula.replace(regexPattern, valueToBeUsed);
          } else if (isRadio === true) {
            let isChecked = $(elem).is(":checked");
            if (isChecked === true) {
              let value = $(elem).data("val");
              formula = formula.replace(regexPattern, value);
            }
          } else if (isLookupElement === true) {
            let lookupelementindex = $(elem).data("lookupelementindex");
            let elementIdentifier = $(elem).data("identifier");
            let elementCalcName = $(elem).data("calcname");
            if (typeof lookupElements[elementIdentifier] === "undefined") {
              var lookupElementProcessedElement: any = {
                elementName: elementIdentifier,
                input1_label: "",
                input1: 0,
                input2_label: "",
                input2: 0,
              };

              if (lookupelementindex === 1) {
                lookupElementProcessedElement.input1 = (parseFloat as any)(
                  $(elem).val(),
                  10,
                );
                lookupElementProcessedElement.input1_label = elementCalcName;
              } else if (lookupelementindex === 2) {
                lookupElementProcessedElement.input2 = (parseFloat as any)(
                  $(elem).val(),
                  10,
                );
                lookupElementProcessedElement.input2_label = elementCalcName;
              }

              lookupElements[elementIdentifier] = lookupElementProcessedElement;
            } else {
              let lookupElementProcessedElement =
                lookupElements[elementIdentifier];
              if (lookupelementindex === 1) {
                lookupElementProcessedElement.input1 = $(elem).val();
                lookupElementProcessedElement.input1_label = elementCalcName;
              } else if (lookupelementindex === 2) {
                lookupElementProcessedElement.input2 = $(elem).val();
                lookupElementProcessedElement.input2_label = elementCalcName;
              }
              //console.log('lookupElementProcessedElement', lookupElementProcessedElement);
            }
          } else {
            //console.log('regexPattern', regexPattern);
            //console.log('formula 5', formula);
            let value: any = $(elem).val();
            value = (parseFloat as any)(value, 10);
            formula = formula.replace(regexPattern, value);
          }
        }
        //If the elemnt is not visible, take the not visible value
        else {
          var elementLookupResult = appState.cpc_data.Elements.filter(function (
            item: any,
            idx: any,
          ) {
            if (item.Name === identifier) return item;
          });

          //If the element is hidden then use default value
          if (elementLookupResult.length > 0) {
            let value = elementLookupResult[0].value_when_not_displayed;
            if (value === undefined) value = 1;

            //Handle special case for Actual value for range input
            if (elementType == "INPUT") {
              let actualValueIdentifier = identifier + "[ActualValue]";
              var actualValueRegex = generateSafeRegex(actualValueIdentifier);
              formula = formula.replace(actualValueRegex, value);
            }

            formula = formula.replace(regexPattern, value);
          }

          //Special handling for lookup element that is hidden
          if (isLookupElement === true) {
            let lookupelementindex = $(elem).data("lookupelementindex");
            let lookupElementName = $(elem).data("identifier");
            let lookupElementCalcName = $(elem).data("calcname");
            let lookupElementResult = appState.cpc_data.Elements.filter(function (
              item: any,
              idx: any,
            ) {
              if (item.Name === lookupElementName) return item;
            });
            if (lookupElementResult.length > 0) {
              let lookupElementIdentifier =
                lookupElementName + "[" + lookupElementCalcName + "]";
              regexPattern = generateSafeRegex(lookupElementIdentifier);
              formula = formula.replace(regexPattern, 0);

              //Update the main element value when both elements are parsed
              if (lookupelementindex === 2) {
                let value = lookupElementResult[0].value_when_not_displayed;
                if (value === undefined) value = 1;
                regexPattern = generateSafeRegex(lookupElementName);
                value = (parseFloat as any)(value, 10);
                formula = formula.replace(regexPattern, value);
              }
            }
          }
        }
      }
    });

    //Now process lookup element
    formula = resolveLookupTables(formula, lookupElements);

    //console.log(formula);
    return formula;
  } catch (e) {
    globalHelper_ReportError(e);
  }
}
